/**
 * Marqueur de run en cours **dans cet onglet** (`sessionStorage`, identifiant de run seul).
 *
 * Il ne contient jamais un jeton, un montant ni un état de jeu : son unique rôle est de détecter
 * un rechargement de page en pleine partie, cas où la run est perdue (CDC §7, DECISIONS §7.4).
 * `sessionStorage` disparaît à la fermeture de l'onglet.
 *
 * Deux écrans s'en servent, et **seulement** pour cette détection :
 * - l'écran de jeu le pose à la création de la run et le retire en sortant (fin de run ou
 *   démontage de l'écran) ;
 * - l'écran d'accueil le **purge** à l'entrée (carte U-02) : l'accueil est l'endroit où l'on
 *   constate qu'aucune partie n'est en cours. Un marqueur oublié (onglet restauré, fermeture
 *   brutale) ne doit jamais produire de message parasite au lancement suivant.
 *
 * Le module est extrait de `screens/jeu.tsx` pour que cette purge soit testable seule et que la
 * clé n'existe qu'à un seul endroit (`cookies/consentement.ts` la référence aussi, comme stockage
 * essentiel).
 *
 * Aucune valeur de progression ne transite par ce marqueur : la progression reste celle du serveur.
 */

/** Clé de session qui marque une run en cours dans cet onglet. */
export const CLE_RUN_EN_COURS = 'elemora.run-en-cours';

/** Lit l'identifiant de run mémorisé, ou `null` (stockage indisponible inclus). */
export function lireMarqueurRun(): string | null {
  try {
    return window.sessionStorage.getItem(CLE_RUN_EN_COURS);
  } catch {
    return null;
  }
}

/**
 * Pose ou retire le marqueur.
 *
 * Un stockage refusé (navigation privée restrictive) laisse simplement la détection de
 * rechargement indisponible : le jeu reste jouable.
 */
export function ecrireMarqueurRun(runId: string | null): void {
  try {
    if (runId === null) window.sessionStorage.removeItem(CLE_RUN_EN_COURS);
    else window.sessionStorage.setItem(CLE_RUN_EN_COURS, runId);
  } catch {
    // Navigation privée restrictive : la détection de rechargement est simplement indisponible.
  }
}

/**
 * Purge l'état de run résiduel de l'onglet (carte U-02).
 *
 * Appelée à l'entrée sur l'accueil : aucune partie n'y est en cours, donc aucun marqueur ne doit
 * y survivre. Sans cette purge, un marqueur oublié ferait afficher « Run perdue » à la place du
 * jeu au lancement suivant, alors que le joueur n'a rien interrompu.
 *
 * Ne touche à aucune donnée de progression : seul l'identifiant de run de session est retiré.
 */
export function purgerMarqueurRun(): void {
  ecrireMarqueurRun(null);
}
